import { z } from 'zod';

import { GLOBAL_MANAGE, type PermissionKey } from '../../permissions/permission-key/permission-key';

/**
 * The roles the platform seeds and depends on. Everything else is a **custom
 * role** created at runtime through the admin role editor (`isSystem = false`)
 * — that is how "a verification clerk who may only review verification cases"
 * is expressed, with no code change (03-access-control).
 *
 * System rows are guarded: their key cannot be renamed and they cannot be
 * deleted, because guards, seeds, and the lockout rules reference them by key.
 */
export const SYSTEM_ROLE_KEYS = [
  'super_admin',
  'admin',
  'reviewer',
  'moderator',
  'member',
] as const;

export const SystemRoleKeySchema = z.enum(SYSTEM_ROLE_KEYS);

export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

/** The role every registered member gets, carrying **zero** catalog permissions (N8). */
export const MEMBER_ROLE_KEY: SystemRoleKey = 'member';

/** The role that bypasses permission evaluation entirely (N22). */
export const SUPER_ADMIN_ROLE_KEY: SystemRoleKey = 'super_admin';

/**
 * The permission set each system role is seeded with.
 *
 * `super_admin` holds the global wildcard **and** bypasses evaluation — the
 * wildcard is what the effective-permissions preview shows, the bypass is what
 * makes a mistaken deny unable to lock the platform out.
 *
 * `admin` deliberately does **not** hold `access.manage` or the global
 * wildcard: managing who else may manage staff is a `super_admin` act. This is
 * a seed, not a law — an owner can grant it to an admin at runtime.
 *
 * `member` is empty and must stay empty: member endpoints authorize by
 * ownership + status + entitlement, never by a catalog permission (N8/N14).
 */
export const SYSTEM_ROLE_PERMISSIONS: Readonly<Record<SystemRoleKey, readonly PermissionKey[]>> = {
  super_admin: [GLOBAL_MANAGE],
  admin: [
    'dashboard.read',
    'access.read',
    'members.manage',
    'verification.manage',
    'trust.manage',
    'questions.manage',
    'settings.manage',
    'integrations.manage',
    'media.review',
    'brand.manage',
    'billing.manage',
    'courses.manage',
    'legal.manage',
    'notifications.manage',
    'moderation.manage',
    'violations.manage',
    'meetings.manage',
    'ai.manage',
    'audit.read',
  ],
  reviewer: [
    'dashboard.read',
    'members.read',
    'verification.review',
    'trust.review',
    'media.review',
  ],
  moderator: ['dashboard.read', 'members.read', 'moderation.review', 'violations.review'],
  member: [],
};

/** Whether a role key is one of the seeded system rows. */
export function isSystemRoleKey(key: string): key is SystemRoleKey {
  return SystemRoleKeySchema.safeParse(key).success;
}
